import Phaser from 'phaser';
import { getWorld, isLevelUnlocked, type Level, type World } from '@/data/worlds';
import { saveSystem } from '@/systems/SaveSystem';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { MuteToggle } from '@/ui/components/MuteToggle';
import { Layout, Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

export interface LevelMapPayload {
  worldId: number;
}

/**
 * LevelMapScene — grade das fases de um mundo.
 *
 * Mostra estrelas obtidas (do save) por fase, destaca o boss e bloqueia
 * fases ainda não desbloqueadas (precisa ≥1★ na anterior). Clicar numa
 * fase desbloqueada inicia a GameScene com o Level.
 */
export class LevelMapScene extends Phaser.Scene {
  private worldId = 1;

  constructor() {
    super({ key: 'LevelMapScene' });
  }

  init(data: LevelMapPayload): void {
    this.worldId = data.worldId;
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const world = getWorld(this.worldId);
    if (!world) {
      // Estado impossível na prática (UI só permite worlds válidos), mas
      // protegemos para não travar o jogo.
      fadeToScene(this, 'WorldMapScene');
      return;
    }

    const { width, height } = this.scale;

    this.add
      .text(width / 2, 56, world.name, {
        fontFamily: Typography.fontFamily,
        fontSize: '44px',
        color: world.accentCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, 100, world.theme, {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.renderLevels(world);

    new Button(this, 96, height - 48, 'Voltar', {
      width: 160,
      height: 48,
      fontSize: 18,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.primary,
      onClick: () => fadeToScene(this, 'WorldMapScene'),
    });

    new MuteToggle(this, this.scale.width - 48, 48);
  }

  private renderLevels(world: World): void {
    const save = saveSystem.getData();
    const { width, height } = this.scale;

    const cols = 3;
    const cardWidth = 280;
    const cardHeight = 130;
    const gapX = 28;
    const gapY = 24;
    const totalWidth = cols * cardWidth + (cols - 1) * gapX;
    const startX = width / 2 - totalWidth / 2;
    const startY = height * 0.22;

    world.levels.forEach((level, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * (cardWidth + gapX) + cardWidth / 2;
      const y = startY + row * (cardHeight + gapY) + cardHeight / 2;

      const stars = save.starsPerLevel[level.id] ?? 0;
      const unlocked = isLevelUnlocked(world, level, save.starsPerLevel);
      this.renderLevelCard(world, level, x, y, cardWidth, cardHeight, stars, unlocked);
    });
  }

  private renderLevelCard(
    world: World,
    level: Level,
    x: number,
    y: number,
    w: number,
    h: number,
    stars: number,
    unlocked: boolean,
  ): void {
    const baseColor = level.isBoss ? this.parseHexCss(Palette.dangerCss) : this.parseHexCss(world.accentCss);
    const bg = this.add.graphics();

    bg.fillStyle(baseColor, 1);
    bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
    if (!unlocked) {
      bg.fillStyle(0x000000, 0.5);
      bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
    }

    const headerLabel = level.isBoss ? `BOSS · Fase ${level.number}` : `Fase ${level.number}`;
    this.add
      .text(x - w / 2 + 20, y - h / 2 + 16, headerLabel, {
        fontFamily: Typography.fontFamily,
        fontSize: '14px',
        color: Palette.textMutedCss,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0);

    this.add
      .text(x - w / 2 + 20, y - h / 2 + 40, level.name, {
        fontFamily: Typography.fontFamily,
        fontSize: '22px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
        wordWrap: { width: w - 40 },
      })
      .setOrigin(0, 0);

    this.add
      .text(x - w / 2 + 20, y + h / 2 - 44, level.flavor, {
        fontFamily: Typography.fontFamily,
        fontSize: '14px',
        color: Palette.textMutedCss,
        wordWrap: { width: w - 40 },
      })
      .setOrigin(0, 0);

    // Estrelas no canto inferior direito
    const starsText = unlocked ? this.starsText(stars) : '🔒';
    this.add
      .text(x + w / 2 - 16, y + h / 2 - 18, starsText, {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(1, 1);

    if (unlocked) {
      const hit = this.add.rectangle(x, y, w, h, 0xffffff, 0).setInteractive({ useHandCursor: true });
      hit.on(Phaser.Input.Events.POINTER_OVER, () => {
        bg.clear();
        bg.lineStyle(3, Palette.textPrimary, 1);
        bg.strokeRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
        bg.fillStyle(baseColor, 1);
        bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
      });
      hit.on(Phaser.Input.Events.POINTER_OUT, () => {
        bg.clear();
        bg.fillStyle(baseColor, 1);
        bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
      });
      hit.on(Phaser.Input.Events.POINTER_UP, () => {
        fadeToScene(this, 'GameScene', { worldId: world.id, levelId: level.id });
      });
    }
  }

  private starsText(stars: number): string {
    const safe = Math.max(0, Math.min(3, stars));
    return '★'.repeat(safe) + '☆'.repeat(3 - safe);
  }

  private parseHexCss(css: string): number {
    return parseInt(css.replace('#', ''), 16);
  }
}
